import React, {useEffect, useState} from 'react';
import Sidebar from '../../../Presentation/components/navigation/Sidebar';
import {useDashboardUIStore} from '../store/dashboardUIStore';
import {useClientTheme} from '../../../core/context/ClientContext';
import {useAuth} from '../../../features/auth/hooks/useAuth';

interface SidebarLayoutProps {
  children: React.ReactNode;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({children}) => {
  const {
    isCollapsed, isMobile, isMobileMenuOpen,
    toggleSidebar, closeMobileMenu,
    setIsMobile, setIsCollapsed, setIsMobileMenuOpen
  } = useDashboardUIStore();
  const theme = useClientTheme();
  const {logout} = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    setLoggingOut(false);
  };

  // Initialize mobile detection on mount and handle window resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(true);
        setIsMobileMenuOpen(false);
      } else {
        // On desktop, ensure sidebar is expanded by default
        setIsCollapsed(false);
        setIsMobileMenuOpen(false);
      }
    };

    // Check on mount
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setIsMobile, setIsCollapsed, setIsMobileMenuOpen]);

  return (
    <div className="h-screen flex font-zain" style={{background: 'var(--color-page-bg, #f2f3f5)'}} dir="rtl">
      {/* Mobile Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div className={`
        ${isMobile ? 'fixed inset-y-0 right-0 z-50' : 'relative z-10'}
        ${isMobile && !isMobileMenuOpen ? 'translate-x-full' : ''}
        transition-transform duration-300 ease-in-out
      `}>
        <Sidebar
          isCollapsed={isCollapsed}
          onToggle={toggleSidebar}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Toolbar */}
        <header className="flex-shrink-0 bg-white shadow-sm border-b border-gray-100 px-4 py-2">
          <div className="flex items-center justify-between h-14">
            {/* Left (RTL: start) — Mobile toggle */}
            <div className="flex items-center gap-3">
              {isMobile && (
                <button
                  onClick={toggleSidebar}
                  style={{color: 'var(--color-primary, #1d4ed8)', background: 'var(--color-secondary, #eff6ff)'}}
                  className="w-10 h-10 flex items-center justify-center rounded-xl transition-colors duration-200 focus:outline-none lg:hidden"
                  aria-label="فتح القائمة"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
            </div>

            {/* Center — Logo + Brand name */}
            <div className="flex items-center gap-3">
              {theme.logoUrl ? (
                <img
                  src={theme.logoUrl}
                  alt={theme.brandName ?? 'Logo'}
                  className="h-10 w-auto object-contain max-w-[160px]"
                />
              ) : (
                <div
                  style={{background: 'var(--color-primary, #1d4ed8)'}}
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                >
                  <span className="text-white font-bold text-sm">
                    {(theme.brandName ?? 'A').charAt(0)}
                  </span>
                </div>
              )}
            </div>

            {/* Right (RTL: end) — Actions */}
            <div className="flex items-center gap-1.5">
              {/* Logout */}
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                title="تسجيل الخروج"
                className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:opacity-50"
              >
                {loggingOut ? (
                  <svg className="animate-spin" width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-3 md:p-6">
          <div className="min-h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SidebarLayout;
